import React, { useState } from 'react';
import { Mail, Paperclip, Tag, Copy, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { DialogContent, DialogDescription, DialogTitle } from './ui/dialog';

export const DEFAULT_AGREEMENT_SUBJECT = 'Partnership Agreement - True Global Education';
export const DEFAULT_AGREEMENT_BODY =
  'Hello,\n\nWelcome aboard as a recruitment partner of True Global Education. Please review and sign the attached partnership agreement at your earliest convenience.\n\nIf you have any questions, simply reply to this email and our partnerships team will get back to you.\n\nWarm regards,\nTrue Global Education';

const ANCHOR_TAGS = [
  { token: '**signature**', label: 'Signature', desc: 'Recruiter signs here', icon: 'sig' },
  { token: '**date**', label: 'Date', desc: 'Auto-fills signing date', icon: 'cal' },
  { token: '**name**', label: 'Full Name', desc: 'Auto-fills signer name', icon: 'usr' },
  { token: '**company**', label: 'Company', desc: 'Editable company name', icon: 'biz' },
  { token: '**title**', label: 'Title / Position', desc: 'Editable role', icon: 'role' },
  { token: '**address**', label: 'Address', desc: 'Signer types their full address', icon: 'addr' },
  { token: '**phone**', label: 'Phone', desc: 'Signer types their phone number', icon: 'phn' },
  { token: '**email**', label: 'Email', desc: 'Signer types or confirms their email', icon: 'eml' },
  { token: '**commission_rate**', label: 'Commission Rate', desc: 'Negotiated %, e.g. "15%"', icon: 'pct' },
  { token: '**effective_date**', label: 'Effective Date', desc: 'Agreement start date', icon: 'eff' },
];

export default function AgreementComposeDialog({ recruiter, form, setForm, sending, onSubmit, onClose }) {
  const [copied, setCopied] = useState('');
  const fileName = form.agreement_pdf?.name || '';
  const toEmail = recruiter.contact_email || '';
  const toName = recruiter.contact_name || '';

  const copyAnchor = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(token);
      setTimeout(() => setCopied(''), 1400);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <DialogContent
      className="max-w-3xl p-0 gap-0 overflow-hidden bg-white border border-tge-navy/10 rounded-2xl shadow-[0_30px_80px_rgba(27,42,107,0.18)]"
      data-testid="agreement-compose-dialog"
    >
      {/* Compose header — like an email client */}
      <div className="flex items-center justify-between px-6 py-4 bg-tge-navy text-white">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-tge-gold/20 flex items-center justify-center">
            <Mail size={16} className="text-tge-gold" />
          </div>
          <div>
            <DialogTitle className="text-white text-base font-semibold tracking-tight">New message · Partnership Agreement</DialogTitle>
            <DialogDescription className="text-[11px] text-tge-cream/70 mt-0.5">
              Sends via DocuSign · {recruiter.company_name}
            </DialogDescription>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition"
          data-testid="agreement-compose-close"
        >
          <X size={16} />
        </button>
      </div>

      <form onSubmit={onSubmit} className="grid lg:grid-cols-[1fr_280px]">
        {/* Left — email fields */}
        <div className="p-6 space-y-3">
          {/* To */}
          <div className="flex items-center gap-3 border-b border-tge-cream pb-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-tge-muted w-14">To</span>
            <div className="flex-1 flex items-center gap-2">
              <span className="inline-flex items-center gap-2 bg-tge-cream-soft border border-tge-cream rounded-full pl-1 pr-3 py-1">
                <span className="w-6 h-6 rounded-full bg-tge-gold/25 text-tge-gold text-[11px] font-semibold flex items-center justify-center">
                  {(toName || toEmail || 'R').charAt(0).toUpperCase()}
                </span>
                <span className="text-[13px] text-tge-navy font-medium">
                  {toName ? `${toName} ` : ''}
                  <span className="text-tge-muted font-normal">&lt;{toEmail || 'no-email-on-file'}&gt;</span>
                </span>
              </span>
            </div>
          </div>

          {/* Subject */}
          <div className="flex items-center gap-3 border-b border-tge-cream pb-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-tge-muted w-14">Subject</span>
            <input
              value={form.email_subject}
              onChange={(e) => setForm({ ...form, email_subject: e.target.value })}
              required
              className="flex-1 bg-transparent border-0 outline-none text-[14px] text-tge-navy placeholder:text-tge-muted/60"
              data-testid="agreement-subject-input"
            />
          </div>

          {/* Body */}
          <div>
            <textarea
              value={form.email_body}
              onChange={(e) => setForm({ ...form, email_body: e.target.value })}
              rows={10}
              placeholder="Write a message…"
              className="w-full text-[14px] text-tge-navy leading-relaxed bg-transparent border-0 outline-none resize-none placeholder:text-tge-muted/60"
              data-testid="agreement-body-textarea"
            />
          </div>

          {/* Attachment row */}
          <div className="border-t border-tge-cream pt-4">
            <label className="flex items-center gap-3 cursor-pointer group" data-testid="agreement-file-label">
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-tge-navy/20 group-hover:border-tge-gold group-hover:bg-tge-gold/5 transition text-[13px] text-tge-navy font-medium">
                <Paperclip size={14} />
                {fileName ? 'Replace agreement PDF' : 'Attach agreement PDF *'}
              </span>
              {fileName && (
                <span className="text-[12px] text-tge-muted truncate max-w-[200px]">
                  · {fileName}
                </span>
              )}
              <input
                type="file"
                accept=".pdf"
                required={!form.agreement_pdf}
                onChange={(e) => setForm({ ...form, agreement_pdf: e.target.files[0] })}
                className="sr-only"
                data-testid="agreement-file-input"
              />
            </label>
          </div>

          {/* Footer actions */}
          <div className="flex items-center justify-between pt-4 border-t border-tge-cream">
            <p className="text-[11px] text-tge-muted">
              Recipient receives this email from DocuSign and can sign the PDF inline.
            </p>
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="rounded-full text-sm" data-testid="agreement-cancel-btn">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={sending || !form.agreement_pdf}
                className="bg-tge-navy hover:bg-tge-navy-deep text-white rounded-full px-5 text-sm shadow-md shadow-tge-navy/20 disabled:opacity-60"
                data-testid="agreement-send-btn"
              >
                {sending ? 'Sending…' : 'Send via DocuSign'}
              </Button>
            </div>
          </div>
        </div>

        {/* Right — anchor strings reference */}
        <aside className="bg-tge-cream-soft border-l border-tge-cream p-5 lg:max-h-[560px] lg:overflow-y-auto">
          <div className="flex items-center gap-2 mb-1">
            <Tag size={13} className="text-tge-gold" />
            <h4 className="text-[11px] font-semibold uppercase tracking-wider text-tge-navy">Anchor tags</h4>
          </div>
          <p className="text-[11px] text-tge-muted leading-relaxed mb-4">
            Place these tokens anywhere inside your PDF. DocuSign auto-replaces them with interactive fields for the signer.
          </p>

          <div className="space-y-2">
            {ANCHOR_TAGS.map((tag) => (
              <button
                key={tag.token}
                type="button"
                onClick={() => copyAnchor(tag.token)}
                className="w-full text-left bg-white hover:bg-white border border-tge-cream hover:border-tge-gold/60 rounded-xl px-3 py-2.5 transition group"
                data-testid={`anchor-tag-${tag.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold text-tge-navy">{tag.label}</div>
                    <code className="text-[11px] text-tge-gold font-mono">{tag.token}</code>
                    <div className="text-[10.5px] text-tge-muted mt-0.5">{tag.desc}</div>
                  </div>
                  <span className="shrink-0 mt-0.5 text-tge-muted group-hover:text-tge-navy transition">
                    {copied === tag.token ? <Check size={14} className="text-emerald-600" /> : <Copy size={13} />}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-5 bg-white border border-tge-cream rounded-xl p-3">
            <div className="text-[10.5px] uppercase tracking-wider text-tge-gold font-semibold mb-1">Tip</div>
            <p className="text-[11px] text-tge-muted leading-relaxed">
              Type the token exactly (with the <code className="text-tge-navy">**</code> on both sides) in your PDF before uploading. Click any tag above to copy it.
              <br /><br />
              <strong className="text-tge-navy">Important:</strong> color the anchor text <span className="bg-gray-200 text-white px-1 rounded">WHITE</span> in your PDF so it's invisible to the signer but readable by DocuSign. Otherwise the raw <code>**name**</code> text will show through on the signed document.
            </p>
          </div>
        </aside>
      </form>
    </DialogContent>
  );
}
